import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import api from '../services/api'
import { analysisApi } from '../services/analysis'
import { ExtractionReview } from '../features/lease/ExtractionReview'
import { ContextCheck } from '../features/lease/ContextCheck'
import { Reviewing } from '../features/lease/Reviewing'
import { RiskReport } from '../features/lease/RiskReport'
import { SaveReport } from '../features/lease/SaveReport'
import { ReportSent } from '../features/lease/ReportSent'
import { ThankYou } from '../features/lease/ThankYou'
import { Screen } from '../components/lease/Screen'
import { BrandHeading } from '../components/lease/BrandHeading'
import { PrimaryButton } from '../components/lease/PrimaryButton'

// Landlord fork of pages/LeaseAnalysis.jsx — same state machine + service
// calls (the backend returns the landlord context-question set automatically
// for is_landlord users), with landlord-voiced copy and the redline download
// wired into the Risk Report instead of the renter's write-request CTA.
function LandlordLeaseAnalysis() {
  const navigate = useNavigate()
  const [file, setFile] = useState(null)
  const [consent, setConsent] = useState(false)
  const [docId, setDocId] = useState(null)
  const [details, setDetails] = useState(null)
  const [detailsConfirmed, setDetailsConfirmed] = useState(false)
  const [reviewing, setReviewing] = useState(false)
  const [failure, setFailure] = useState(null)
  const [report, setReport] = useState(null)
  const [uploadPct, setUploadPct] = useState(null)
  const [endStep, setEndStep] = useState(null)
  const [sentTo, setSentTo] = useState(null)

  const uploadMutation = useMutation({
    mutationFn: async () => {
      const form = new FormData()
      form.append('file', file)
      form.append('consent_attorney_review', consent ? 'true' : 'false')
      const doc = await api
        .post('/v1/pdf/upload', form, {
          onUploadProgress: (e) => {
            if (e.total) setUploadPct(Math.round((e.loaded / e.total) * 100))
          },
        })
        .then((r) => r.data)
      const d = await analysisApi.getDetails(doc.id)
      return { id: doc.id, details: d }
    },
    onSuccess: ({ id, details }) => {
      setDocId(id)
      setDetails(details)
      setUploadPct(null)
    },
    onError: () => setUploadPct(null),
  })

  const confirmMutation = useMutation({
    mutationFn: () => analysisApi.confirmDetails(docId),
    onSuccess: () => setDetailsConfirmed(true),
  })

  const analyzeMutation = useMutation({
    mutationFn: () => analysisApi.analyze(docId),
    onSuccess: () => {
      setFailure(null)
      setReviewing(true)
    },
  })

  const handleAnalysisComplete = async () => {
    const doc = await analysisApi.getDocument(docId)
    setReviewing(false)
    setReport(doc.analysis_results)
  }

  const handleAnalysisFailed = (message) => {
    setReviewing(false)
    setFailure(message)
  }

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) setFile(e.target.files[0])
  }

  const handleUpload = (e) => {
    e.preventDefault()
    if (file) uploadMutation.mutate()
  }

  if (report && endStep === 'thanks') {
    return <ThankYou />
  }

  if (report && endStep === 'sent') {
    return (
      <ReportSent
        email={sentTo}
        onReturnToReport={() => setEndStep(null)}
        onDone={() => setEndStep('thanks')}
      />
    )
  }

  if (report && endStep === 'save') {
    return (
      <SaveReport
        documentId={docId}
        onSent={(email) => {
          setSentTo(email)
          setEndStep('sent')
        }}
        onReturnToReport={() => setEndStep(null)}
        onDone={() => setEndStep('thanks')}
      />
    )
  }

  if (report) {
    return (
      <RiskReport
        report={report}
        details={details}
        title="Compliance Review"
        questionsHeading="Points to confirm before you use this lease"
        attorneyCtaLabel="Have an attorney review this"
        onDownload={() => analysisApi.downloadReport(docId)}
        onDownloadRedline={() => analysisApi.downloadRedline(docId)}
        onSaveReport={() => setEndStep('save')}
        onFindAttorney={() => navigate('/attorneys', { state: { documentId: docId } })}
      />
    )
  }

  if (reviewing) {
    return (
      <Reviewing
        documentId={docId}
        onComplete={handleAnalysisComplete}
        onFailed={handleAnalysisFailed}
        title="Reviewing your lease template"
      />
    )
  }

  if (details) {
    return (
      <Screen>
        {!detailsConfirmed ? (
          <ExtractionReview details={details} onConfirm={() => confirmMutation.mutate()} />
        ) : (
          <ContextCheck documentId={docId} onComplete={() => analyzeMutation.mutate()} />
        )}
        {analyzeMutation.isPending && (
          <p className="mt-4 text-center text-slate-600 dark:text-slate-300">Starting your review…</p>
        )}
        {analyzeMutation.isError && (
          <p className="mt-4 text-center text-rose-600 dark:text-rose-400">{analyzeMutation.error.message}</p>
        )}
        {failure && (
          <div className="mt-4 rounded-2xl bg-rose-50 p-4 text-rose-700 ring-1 ring-rose-100 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-900">
            <p>Review failed: {failure}</p>
            <p className="mt-1 text-sm">
              Your credit was not used. You can try again — if it keeps failing, contact{' '}
              <a className="underline" href="mailto:support@coloradoleasecheck.com">
                support@coloradoleasecheck.com
              </a>
              .
            </p>
            <PrimaryButton onClick={() => analyzeMutation.mutate()} className="mt-3">
              Retry review
            </PrimaryButton>
          </div>
        )}
      </Screen>
    )
  }

  return (
    <Screen>
      <header className="animate-fade-in text-center">
        <BrandHeading className="text-4xl">Upload your lease template</BrandHeading>
        <p className="mt-3 text-slate-600 dark:text-slate-300">
          Upload your established lease to screen it against Colorado landlord-tenant law.
        </p>
      </header>

      <div className="mt-8 animate-slide-in rounded-2xl bg-white/70 p-4 shadow-sm ring-1 ring-emerald-100 backdrop-blur dark:bg-slate-800/60 dark:ring-slate-700">
        <h3 className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">What we'll check</h3>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
          After you upload, we'll pull the key details from your lease for you to confirm,
          ask a few quick context questions, then screen it against Colorado compliance
          rules and prepare a redline draft.
        </p>
      </div>

      <form onSubmit={handleUpload} className="mt-6 animate-slide-in space-y-5">
        <div className="relative rounded-2xl border-2 border-dashed border-emerald-300 bg-white/50 p-10 text-center backdrop-blur transition hover:border-emerald-500 dark:border-emerald-700 dark:bg-slate-800/40">
          <input
            type="file"
            onChange={handleFileChange}
            accept=".pdf"
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          />
          <div className="text-slate-600 dark:text-slate-300">
            {file ? (
              <span className="font-semibold text-emerald-700 dark:text-emerald-400">{file.name}</span>
            ) : (
              <span className="font-medium">Drop your PDF here, or click to select</span>
            )}
          </div>
        </div>

        {uploadPct !== null && (
          <div className="h-2 w-full overflow-hidden rounded-full bg-emerald-100 dark:bg-slate-700">
            <div
              className="h-full rounded-full bg-gradient-to-r from-emerald-600 to-teal-600 transition-all"
              style={{ width: `${uploadPct}%` }}
            />
          </div>
        )}

        <label className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-1 h-4 w-4 accent-emerald-600"
          />
          <span>
            I understand this is not legal advice and consent to having this lease
            screened for common compliance and risk issues.
          </span>
        </label>

        <PrimaryButton type="submit" disabled={!file || uploadMutation.isPending}>
          {uploadMutation.isPending ? 'Uploading…' : 'Upload & Review'}
        </PrimaryButton>
      </form>

      {uploadMutation.isError && (
        <div className="mt-6 animate-slide-in rounded-2xl bg-rose-50 p-4 text-rose-700 ring-1 ring-rose-100 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-900">
          <p>{uploadMutation.error.message}</p>
        </div>
      )}
    </Screen>
  )
}

export default LandlordLeaseAnalysis
